import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Send, User, MessageSquare, Paperclip, Crown, Search,
  Check, CheckCheck, Circle, ArrowLeft, FileText, Image,
  X, Trash2, Download
} from 'lucide-react';
import { SEOHead } from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = (now - date) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 172800) return 'hier';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const formatMessageTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const formatDateSeparator = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (date.toDateString() === yesterday.toDateString()) return 'Hier';
  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

const isNewDay = (msg, prevMsg) => {
  if (!prevMsg) return true;
  const d1 = new Date(msg.created_at).toDateString();
  const d2 = new Date(prevMsg.created_at).toDateString();
  return d1 !== d2;
};

const MessagesPage = () => {
  const { t } = useTranslation();
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const { user, isPremium } = useAuth();
  const { isConnected, typingUsers, sendTyping, addMessageHandler, addNotificationHandler, addPresenceHandler, addReadReceiptHandler, refreshUnread } = useWebSocket();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlineUsers, setOnlineUsers] = useState({});
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const scrollAreaRef = useRef(null);

  // Fetch conversations
  const fetchConversations = useCallback(async (silent = false) => {
    try {
      const res = await axios.get(`${API}/messages/conversations`, { withCredentials: true });
      setConversations(res.data || []);
      const onMap = {};
      (res.data || []).forEach(c => { onMap[c.partner_id] = c.is_online; });
      setOnlineUsers(prev => ({ ...prev, ...onMap }));
    } catch (error) {
      if (!silent) console.error('Error fetching conversations:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPremium) {
      fetchConversations();
    } else {
      setLoading(false);
    }
  }, [isPremium, fetchConversations]);

  // Handle URL partner ID
  useEffect(() => {
    if (partnerId) {
      setSelectedPartner(partnerId);
      fetchMessages(partnerId);
      fetchPartnerInfo(partnerId);
      setShowMobileChat(true);
    }
  }, [partnerId, fetchMessages, fetchPartnerInfo]);

  // Listen for real-time messages
  useEffect(() => {
    if (!addMessageHandler) return;
    const removeHandler = addMessageHandler((messageData) => {
      if (messageData.type === 'message_deleted') {
        setMessages(prev => prev.filter(m => m.id !== messageData.message_id));
        return;
      }
      if (messageData.sender_id === selectedPartner || messageData.recipient_id === selectedPartner) {
        setMessages(prev => {
          if (prev.some(m => m.id === messageData.id)) return prev;
          return [...prev, messageData];
        });
        scrollToBottom();
        if (messageData.sender_id === selectedPartner) {
          markAsRead(selectedPartner);
          refreshUnread();
        }
      }
      fetchConversations(true);
    });
    return () => removeHandler();
  }, [addMessageHandler, selectedPartner, fetchConversations, refreshUnread, markAsRead]);

  // Listen for presence changes
  useEffect(() => {
    if (!addPresenceHandler) return;
    const removeHandler = addPresenceHandler((data) => {
      setOnlineUsers(prev => ({ ...prev, [data.user_id]: data.is_online }));
    });
    return () => removeHandler();
  }, [addPresenceHandler]);

  // Listen for read receipts
  useEffect(() => {
    if (!addReadReceiptHandler) return;
    const removeHandler = addReadReceiptHandler((data) => {
      if (data.reader_id === selectedPartner) {
        setMessages(prev => prev.map(m =>
          m.sender_id === user?.id && !m.is_read
            ? { ...m, is_read: true, read_at: data.read_at }
            : m
        ));
      }
    });
    return () => removeHandler();
  }, [addReadReceiptHandler, selectedPartner, user?.id]);

  // Periodic refresh for online status
  useEffect(() => {
    if (!isPremium) return;
    const interval = setInterval(() => fetchConversations(true), 15000);
    return () => clearInterval(interval);
  }, [isPremium, fetchConversations]);

  const fetchMessages = useCallback(async (pid) => {
    try {
      const res = await axios.get(`${API}/messages/${pid}`, { withCredentials: true });
      setMessages(res.data || []);
      scrollToBottom();
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, []);

  const fetchPartnerInfo = useCallback(async (pid) => {
    try {
      const res = await axios.get(`${API}/users/${pid}`);
      setPartnerInfo(res.data);
    } catch (error) {
      console.error('Error fetching partner info:', error);
    }
  }, []);

  const markAsRead = useCallback(async (pid) => {
    try {
      await axios.post(`${API}/messages/${pid}/read`, {}, { withCredentials: true });
      refreshUnread();
    } catch (e) {
      console.error('Error marking as read:', e);
    }
  }, [refreshUnread]);

  const selectConversation = (pid, partnerName) => {
    setSelectedPartner(pid);
    setPartnerInfo({ company_name: partnerName });
    fetchMessages(pid);
    fetchPartnerInfo(pid);
    markAsRead(pid);
    setShowMobileChat(true);
    // Update URL without full navigation
    window.history.replaceState(null, '', `/dashboard/messages/${pid}`);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    if (selectedPartner && sendTyping) {
      sendTyping(selectedPartner, true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => sendTyping(selectedPartner, false), 2000);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPartner) return;

    setSending(true);
    if (sendTyping) sendTyping(selectedPartner, false);

    try {
      const res = await axios.post(`${API}/messages`, {
        recipient_id: selectedPartner,
        content: newMessage.trim()
      }, { withCredentials: true });

      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      scrollToBottom();
      fetchConversations(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/messages/upload-attachment`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { url, name, type } = uploadRes.data;
      const isImage = type.startsWith('image/');

      const res = await axios.post(`${API}/messages`, {
        recipient_id: selectedPartner,
        content: isImage ? `[Image] ${name}` : `[Fichier] ${name}`,
        attachment_url: url,
        attachment_name: name,
        attachment_type: type
      }, { withCredentials: true });

      setMessages(prev => [...prev, res.data]);
      scrollToBottom();
      fetchConversations(true);
    } catch (error) {
      toast.error("Erreur lors de l'envoi du fichier");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/messages/${messageId}`, { withCredentials: true });
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleBackToList = () => {
    setShowMobileChat(false);
    setSelectedPartner(null);
    window.history.replaceState(null, '', '/dashboard/messages');
  };

  const isPartnerTyping = selectedPartner && typingUsers[selectedPartner];
  const isPartnerOnline = selectedPartner && onlineUsers[selectedPartner];
  const filteredConversations = searchQuery
    ? conversations.filter(c => c.partner_name.toLowerCase().includes(searchQuery.toLowerCase()))
    : conversations;
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  if (!isPremium) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-8">
        <SEOHead title="Messagerie" path="/dashboard/messages" noindex />
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-[#1E3A8A]/10 rounded-full mx-auto mb-6 flex items-center justify-center">
            <MessageSquare className="w-10 h-10 text-[#1E3A8A]" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-[#111827] mb-4">Messagerie Premium</h1>
          <p className="text-[#4B5563] mb-6">
            La messagerie directe est réservée aux membres Premium. Passez à Premium pour contacter directement les entreprises et sous-traitants.
          </p>
          <Link to="/dashboard/subscription">
            <Button className="btn-accent" data-testid="upgrade-premium-btn">
              <Crown className="w-4 h-4 mr-2" />
              Passer à Premium
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="messages-page">
      <SEOHead title="Messagerie" path="/dashboard/messages" noindex />
      <div className="max-w-6xl mx-auto px-0 sm:px-4 lg:px-8 py-0 sm:py-6">
        {/* Mobile header */}
        <div className="sm:hidden flex items-center justify-between px-4 py-3 bg-white border-b">
          {showMobileChat ? (
            <>
              <Button variant="ghost" size="sm" onClick={handleBackToList} data-testid="back-to-conversations">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold text-sm truncate max-w-[180px]">
                  {partnerInfo?.company_name || 'Chat'}
                </span>
                {isPartnerOnline && <Circle className="w-2.5 h-2.5 fill-emerald-500 text-emerald-500" />}
              </div>
              <div className="w-8" />
            </>
          ) : (
            <>
              <h1 className="font-heading font-bold text-lg">{t('nav.messages')}</h1>
              {totalUnread > 0 && (
                <Badge className="bg-[#F59E0B] text-white text-xs">{totalUnread}</Badge>
              )}
            </>
          )}
        </div>

        {/* Desktop header */}
        <div className="hidden sm:flex items-center justify-between mb-6 px-0">
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-bold text-3xl text-[#111827]" data-testid="messages-title">
              {t('nav.messages')}
            </h1>
            {totalUnread > 0 && (
              <Badge className="bg-[#F59E0B] text-white">{totalUnread} non lu{totalUnread > 1 ? 's' : ''}</Badge>
            )}
          </div>
          {isConnected && (
            <Badge className="bg-emerald-100 text-emerald-700" data-testid="ws-connected-badge">
              <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse" />
              Connecté
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 sm:gap-4 h-[calc(100vh-10rem)] sm:h-[640px]">
          {/* Conversations List */}
          <Card className={`md:col-span-1 rounded-none sm:rounded-lg border-0 sm:border ${showMobileChat ? 'hidden md:block' : ''}`}>
            <CardContent className="p-0 h-full flex flex-col">
              <div className="p-3 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher..."
                    className="pl-9 h-9 text-sm bg-gray-50 border-gray-200"
                    data-testid="search-conversations"
                  />
                </div>
              </div>
              <ScrollArea className="flex-1">
                {loading ? (
                  <div className="p-4 text-center text-[#4B5563]">{t('common.loading')}</div>
                ) : filteredConversations.length > 0 ? (
                  filteredConversations.map((conv) => (
                    <button
                      key={conv.partner_id}
                      onClick={() => selectConversation(conv.partner_id, conv.partner_name)}
                      className={`w-full p-3 text-left border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                        selectedPartner === conv.partner_id ? 'bg-[#1E3A8A]/5 border-l-2 border-l-[#1E3A8A]' : ''
                      }`}
                      data-testid={`conversation-${conv.partner_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative flex-shrink-0">
                          <div className="w-10 h-10 bg-[#1E3A8A]/10 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-[#1E3A8A]" />
                          </div>
                          {conv.is_online && (
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <p className={`text-sm truncate ${conv.unread_count > 0 ? 'font-bold text-[#111827]' : 'font-medium text-[#374151]'}`}>
                              {conv.partner_name}
                            </p>
                            <span className="text-[10px] text-[#9CA3AF] flex-shrink-0">
                              {formatRelativeTime(conv.last_message_time)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-1">
                            <p className={`text-xs truncate ${conv.unread_count > 0 ? 'text-[#111827] font-medium' : 'text-[#9CA3AF]'}`}>
                              {conv.last_message_sender_id === user?.id && (
                                <span className="text-[#9CA3AF] mr-1">Vous :</span>
                              )}
                              {conv.last_message}
                            </p>
                            {conv.unread_count > 0 && (
                              <span className="flex-shrink-0 w-5 h-5 bg-[#1E3A8A] text-white rounded-full text-[10px] flex items-center justify-center font-bold">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-6 text-center text-[#9CA3AF]">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{searchQuery ? 'Aucun résultat' : 'Aucune conversation'}</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Chat Area */}
          <Card className={`md:col-span-2 rounded-none sm:rounded-lg border-0 sm:border ${!showMobileChat && !selectedPartner ? 'hidden md:block' : ''}`}>
            <CardContent className="p-0 flex flex-col h-full">
              {selectedPartner ? (
                <>
                  {/* Chat Header */}
                  <div className="hidden sm:flex p-3 border-b border-gray-100 items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 bg-[#1E3A8A]/10 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-[#1E3A8A]" />
                        </div>
                        {isPartnerOnline && (
                          <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
                        )}
                      </div>
                      <div>
                        <h2 className="font-heading font-bold text-sm">{partnerInfo?.company_name || 'Conversation'}</h2>
                        {isPartnerTyping ? (
                          <p className="text-xs text-[#F59E0B] animate-pulse">écrit...</p>
                        ) : isPartnerOnline ? (
                          <p className="text-xs text-emerald-600">En ligne</p>
                        ) : (
                          <p className="text-xs text-[#9CA3AF]">Hors ligne</p>
                        )}
                      </div>
                    </div>
                    {partnerInfo?.id && (
                      <Link to={`/contractors/${partnerInfo.id}`}>
                        <Button variant="ghost" size="sm" className="text-xs" data-testid="view-profile-btn">Voir profil</Button>
                      </Link>
                    )}
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
                    <div className="space-y-1">
                      {messages.map((msg, idx) => {
                        const isMine = msg.sender_id === user?.id;
                        const showDateSep = isNewDay(msg, messages[idx - 1]);
                        return (
                          <React.Fragment key={msg.id || idx}>
                            {showDateSep && (
                              <div className="flex items-center justify-center my-4">
                                <span className="text-[10px] text-[#9CA3AF] bg-gray-100 px-3 py-1 rounded-full">
                                  {formatDateSeparator(msg.created_at)}
                                </span>
                              </div>
                            )}
                            <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} group`}>
                              <div className={`max-w-[75%] relative ${isMine ? 'order-1' : ''}`}>
                                {/* Attachment */}
                                {msg.attachment_url && (
                                  <div className={`mb-1 rounded-lg overflow-hidden ${isMine ? 'bg-[#1E3A8A]' : 'bg-gray-100'}`}>
                                    {msg.attachment_type?.startsWith('image/') ? (
                                      <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer">
                                        <img loading="lazy"
                                          src={msg.attachment_url}
                                          alt={msg.attachment_name || 'Image'}
                                          className="max-w-full max-h-48 object-cover rounded-lg"
                                        />
                                      </a>
                                    ) : (
                                      <a
                                        href={msg.attachment_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-2 px-3 py-2 text-xs ${isMine ? 'text-white/90' : 'text-[#374151]'}`}
                                      >
                                        <FileText className="w-4 h-4 flex-shrink-0" />
                                        <span className="truncate">{msg.attachment_name || 'Fichier'}</span>
                                        <Download className="w-3.5 h-3.5 flex-shrink-0" />
                                      </a>
                                    )}
                                  </div>
                                )}
                                {/* Message bubble */}
                                {msg.content && !msg.content.startsWith('[Image]') && !msg.content.startsWith('[Fichier]') && (
                                  <div
                                    className={`px-3 py-2 rounded-2xl text-sm ${
                                      isMine
                                        ? 'bg-[#1E3A8A] text-white rounded-br-md'
                                        : 'bg-gray-100 text-[#111827] rounded-bl-md'
                                    }`}
                                  >
                                    <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                                  </div>
                                )}
                                {/* Meta: time + read status */}
                                <div className={`flex items-center gap-1 mt-0.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
                                  <span className="text-[10px] text-[#9CA3AF]">
                                    {formatMessageTime(msg.created_at)}
                                  </span>
                                  {isMine && (
                                    msg.is_read ? (
                                      <CheckCheck className="w-3 h-3 text-[#1E3A8A]" />
                                    ) : (
                                      <Check className="w-3 h-3 text-[#9CA3AF]" />
                                    )
                                  )}
                                  {isMine && (
                                    <button
                                      onClick={() => handleDeleteMessage(msg.id)}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="w-3 h-3 text-[#9CA3AF] hover:text-red-500" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      {isPartnerTyping && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 text-[#9CA3AF] px-4 py-2.5 rounded-2xl rounded-bl-md">
                            <div className="flex gap-1">
                              <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Input */}
                  <form onSubmit={handleSend} className="p-3 border-t border-gray-100 bg-white">
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleFileUpload}
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="flex-shrink-0 h-9 w-9 text-[#9CA3AF] hover:text-[#1E3A8A]"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="attach-file-btn"
                      >
                        {uploading ? (
                          <div className="w-4 h-4 border-2 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Paperclip className="w-4 h-4" />
                        )}
                      </Button>
                      <Input
                        value={newMessage}
                        onChange={handleInputChange}
                        placeholder="Écrivez votre message..."
                        className="flex-1 h-9 text-sm border-gray-200 focus:border-[#1E3A8A] rounded-full px-4"
                        disabled={sending || uploading}
                        data-testid="message-input"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend(e);
                          }
                        }}
                      />
                      <Button
                        type="submit"
                        size="icon"
                        className="flex-shrink-0 h-9 w-9 rounded-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90"
                        disabled={sending || uploading || !newMessage.trim()}
                        data-testid="send-message-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-[#9CA3AF]">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-20" />
                    <p className="text-sm font-medium">Sélectionnez une conversation</p>
                    <p className="text-xs mt-1">ou envoyez un message depuis le profil d'un sous-traitant</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
